import React, { useState } from "react";
import { EDataTable } from "../../../components/EDataTable";
import { IconButton, IconTextButton, TextButton } from "../../../components/ButtonComponents";
import { TableActions } from "../../../components/TableActions";
import { Trash2, Plus, Info, Edit, ArrowRightLeft } from "lucide-react"; 
import { toast } from "react-toastify";

import { StockMovement } from "../../../types/stockMovement"; 
import { useStockMovementStore } from "../../../store/stockMovementStore"; 
import { useConfirmModal } from "../../../hooks/useConfirmModal";
import { useApiErrorHandler } from "../../../hooks/useApiErrorHandler";
import { DataTableColumn, SimpleFilter } from "../../../types"; 
import { usePermission } from "../../../hooks/usePermission";

// Dialoglarımızı import ediyoruz
import { WarehouseMovementDialog } from "./WarehouseMovementDialog";
import { WarehouseMovementViewDialog } from "./WarehouseMovementViewDialog";
import { formatQuantity } from "../../../utils/format";

export const WarehouseTransferList = () => {
    const store = useStockMovementStore(); 
    const { handleApiError } = useApiErrorHandler();
    const { confirm, modal: confirmModal } = useConfirmModal();
    const { canCreate, canUpdate, canDelete } = usePermission('stockModule');
    
    // --- State Yönetimi ---
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [isViewOpen, setIsViewOpen] = useState(false);
    const [selectedData, setSelectedData] = useState<any>(null);

    // --- Handlers ---
    const handleSaveMovement = async (payload: any) => {
        try {
            // Transfer işlemi backend'de özel bir endpoint gerektirebilir (çift kayıt için)
            if (payload.type === 'TRANSFER') {
                // await store.transfer(payload); // Store'da tanımlandığında aktif edilecek
            } else {
                if (payload.id) {
                    await store.updateItem(payload.id, payload);
                } else {
                    await store.createItem(payload);
                }
            }
            toast.success("İşlem başarıyla kaydedildi.");
            handleClose();
        } catch (error) {
            handleApiError(error);
        }
    };

    const handleEdit = (data: StockMovement) => {
        setSelectedData(data);
        setIsEditOpen(true);
    };

    const handleView = (data: StockMovement) => {
        setSelectedData(data);
        setIsViewOpen(true);
    };

    const handleClose = () => {
        setIsEditOpen(false);
        setIsViewOpen(false);
        setSelectedData(null);
    };

    const handleDelete = async (m: StockMovement) => {
        const ok = await confirm(`${m.reference} referanslı stok hareketi silinecek. Emin misiniz?`);
        if (ok) {
            try {
                await store.deleteItem(m.id);
                toast.success("Hareket silindi.");
            } catch (e) {
                handleApiError(e);
            }
        }
    };

    // --- Kolon Tanımları ---
    const columns: DataTableColumn<StockMovement>[] = [
        { 
            field: "reference", 
            header: "Ref / Fiş No", 
            width: "180px",
            sortable: true,
            body: (m) => (
                <div className="flex flex-col">
                    <span className="font-mono font-bold text-blue-600">{m.reference || "#NO-REF"}</span>
                    {m.accountMovementId && <span className="text-[10px] text-gray-400">Fatura Bağlantılı</span>}
                </div>
            )
        },
        { 
            field: "createdAt", 
            header: "Tarih", 
            width: "120px",
            body: (m) => new Date(m.createdAt).toLocaleDateString('tr-TR') 
        },
        { 
            field: "warehouseId", 
            header: "Depo", 
            width: "250px",
            body: (m) => {
                const isOut = m.type?.toUpperCase() === 'OUT' || m.quantity < 0;
                return (
                <div className="flex items-center gap-2">
                    {isOut ? (
                        <span className="text-red-500 font-medium bg-red-50 px-2 py-0.5 rounded text-[11px]">ÇIKIŞ</span>
                    ) : (
                        <span className="text-green-500 font-medium bg-green-50 px-2 py-0.5 rounded text-[11px]">GİRİŞ</span>
                    )}
                    <span className=" ">{m.warehouse?.name}</span>
                </div>
                );
            }
        },
        { 
            field: "stock",
            header: "Ürün / Varyant",
            width: "300px",
            body: (m) => (
                <div className="flex flex-col">
                    <span className="font-medium  ">{m.stock?.name}</span>
                    {m.variant && <span className="text-[10px] text-blue-500 font-bold uppercase">{m.variant.name}</span>}
                </div>
            )
        },
        { 
            field: "quantity", 
            header: "Miktar", 
            width: "150px",
            align: "center",
            body: (m) => {
                const isOut = m.type?.toUpperCase() === 'OUT' || m.quantity < 0;
                return (
                <span className={`font-bold ${isOut ? 'text-red-600' : 'text-green-600'}`}>
                    {formatQuantity(Math.abs(m.quantity))} {m.stock.unit?.name??""}
                </span>
                );
            }
        },
        { 
            field: "actions", 
            header: "İşlemler", 
            width: "150px",
            align: "center",
            body: (m: StockMovement) => {
                const isSystemGenerated = !!m.accountMovementId;

                return (
                    <TableActions
                        actions={[
                            {
                                icon: Info,
                                colorClass: "text-gray-400 hover:bg-gray-50 hover:text-blue-500",
                                title: "Detayları Görüntüle",
                                onClick: () => handleView(m)
                            },
                            {
                                icon: Edit,
                                colorClass: "text-blue-600 hover:bg-blue-50",
                                title: "Düzenle",
                                onClick: () => handleEdit(m),
                                show: !isSystemGenerated && !!canUpdate
                            },
                            {
                                icon: Trash2,
                                colorClass: "text-red-500 hover:bg-red-50",
                                title: "Sil",
                                onClick: () => handleDelete(m),
                                show: !isSystemGenerated && !!canDelete
                            }
                        ]}
                    />
                );
            } 
        },
    ];

    return (
        <div className="w-full">
            {confirmModal}
            
            <EDataTable<StockMovement> 
            tableKey="warehouse_transfer_list"
                useStore={() => store} 
                dataKey="id"
                title="Stok Hareketleri"
                placeholder="Fiş No veya Ürün Ara..."
                searchFields={[
                    { field: "reference", operator: "contains" as SimpleFilter["operator"] }, 
                ]}
                actions={
                    canCreate ? (
                        <div className="flex gap-2">
                            <IconTextButton icon={Plus} onClick={() => setIsEditOpen(true)} className="btn-new ">
                                Yeni İşlem
                            </IconTextButton>
                        </div>
                    ) : undefined
                }
                columns={columns as any}
            /> 

            {/* Düzenleme ve Yeni Kayıt Dialogu */}
            <WarehouseMovementDialog
                isOpen={isEditOpen}
                onClose={handleClose}
                onSave={handleSaveMovement}
                initialData={selectedData} 
            />

            {/* Sadece Görüntüleme Dialogu (Sistem Kayıtları İçin) */}
            <WarehouseMovementViewDialog
                isOpen={isViewOpen}
                onClose={handleClose}
                data={selectedData}
            />
        </div>
    );
};